import { useEffect, useRef, useState } from "react";
import type { PhysicalMaterialParams } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useCollapsed, useDomainState, useEditorStore } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { Icon } from "../../../kit";
import { CompactCheck, CompactColor, CompactNumber, CompactSelect, CompactSlider, Row } from "../../inspector/rows";


const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Material write failed.";

/** How long to wait after the `material` slice moves before re-reading the
 * physical params. Long enough that a slider drag in the sibling "Material"
 * section (one write per frame) costs a single read, not one per frame. */
const RESYNC_DELAY_MS = 300;

/**
 * Full MeshPhysicalMaterial editor (v14.3, iJewel Part-4). Seeds from
 * getGroupPhysicalParams for the current selection and writes every change
 * through setGroupPhysicalParams, reconciling local state from the canonical
 * full params the setter returns. Gem groups return null (no physical
 * material) — they stay on the Diamond ray-trace surface above.
 *
 * The Picking tab's "Material" section edits the SAME MeshPhysicalMaterial via
 * the `material` domain, so this editor keeps both directions in sync: it
 * refreshes that slice after its own writes, and re-reads itself when the slice
 * moves underneath it (a preset apply, a .pmat import, or a drag over there).
 */
export function FullMaterialEditor() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const store = useEditorStore();
  const selId = useDomainState("selection");
  const materialSlice = useDomainState("material");
  const [params, setParams] = useState<PhysicalMaterialParams | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Writes of ours still in flight — a resync read must not overtake them. */
  const pending = useRef(0);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    setError(null);
    if (!selId) {
      setParams(null);
      setLoaded(true);
      return;
    }
    r.getGroupPhysicalParams(selId)
      .then((p) => {
        if (!alive) return;
        setParams(p);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // Without this the failure rendered as "Gem materials use the Diamond
        // section above." — a wrong explanation for a dead RPC.
        setParams(null);
        setError(errText(e));
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [r, selId]);

  // Re-read when the `material` slice changes under us. Skipped while one of
  // our own writes is in flight, so a drag here is never yanked back by a read
  // that started before the last write landed; the refresh that write triggers
  // re-arms this timer, so the final value still gets reconciled.
  useEffect(() => {
    void materialSlice;
    if (!selId) return;
    let alive = true;
    const t = setTimeout(() => {
      if (pending.current > 0) return;
      r.getGroupPhysicalParams(selId).then(
        (p) => { if (alive && p) setParams(p); },
        (e: unknown) => { if (alive) setError(errText(e)); },
      );
    }, RESYNC_DELAY_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [materialSlice, r, selId]);

  if (!selId) return <Note text="Select a part to edit its material." />;
  if (!loaded) return null;
  if (!params) {
    return error ? (
      <span className="block px-3 py-1.5 text-[11px] text-ed-red">{error}</span>
    ) : (
      <Note text="Gem materials use the Diamond section above." />
    );
  }

  const set = <K extends keyof PhysicalMaterialParams>(field: K, value: PhysicalMaterialParams[K]) => {
    setError(null);
    pending.current += 1;
    void r.setGroupPhysicalParams(selId, { [field]: value }).then(
      (p) => {
        pending.current -= 1;
        if (p) setParams(p);
        // The "Material" section renders the same colour/roughness/metalness off
        // the `material` slice; without this resync it keeps showing pre-edit
        // values until the selection changes. (The slice has a seed now, so the
        // refresh genuinely re-reads the viewer.)
        void store.refresh("material");
      },
      (e: unknown) => {
        pending.current -= 1;
        // A rejected write used to vanish: the row kept the value the viewer
        // never accepted and nothing said so.
        setError(errText(e));
      },
    );
  };

  return (
    <div className="flex flex-col">
      <Group title="Colors">
      <Row label="Colour"><CompactColor value={params.color} onChange={(v) => set("color", v)} /></Row>
      <Row label="Opacity"><CompactSlider value={params.opacity} min={0} max={1} step={0.01} onChange={(v) => set("opacity", v)} /></Row>
      <Row label="Transparent"><CompactCheck checked={params.transparent} onChange={(v) => set("transparent", v)} /></Row>
      <Row label="Side">
        <CompactSelect
          value={params.side}
          options={[
            { id: "front", label: "Front" },
            { id: "back", label: "Back" },
            { id: "double", label: "Double" },
          ]}
          onChange={(v) => set("side", v as PhysicalMaterialParams["side"])}
        />
      </Row>
      <Row label="Flat Shading"><CompactCheck checked={params.flatShading} onChange={(v) => set("flatShading", v)} /></Row>

      </Group>
      <Group title="Rough / Metal">
      <Row label="Roughness"><CompactSlider value={params.roughness} min={0} max={1} step={0.01} onChange={(v) => set("roughness", v)} /></Row>
      <Row label="Metalness"><CompactSlider value={params.metalness} min={0} max={1} step={0.01} onChange={(v) => set("metalness", v)} /></Row>
      <Row label="IOR"><CompactSlider value={params.ior} min={0} max={4} step={0.01} onChange={(v) => set("ior", v)} /></Row>
      <Row label="Reflectivity"><CompactSlider value={params.reflectivity} min={0} max={1} step={0.01} onChange={(v) => set("reflectivity", v)} /></Row>
      {caps.v31 ? (
        <Row label="Override Environment" hint="envMapIntensity is absolute, not multiplied by the scene environment intensity.">
          <CompactCheck checked={!!params.overrideEnvironment} onChange={(v) => set("overrideEnvironment", v)} />
        </Row>
      ) : null}

      </Group>
      <Group title="Specular">
      <Row label="Specular Intensity"><CompactSlider value={params.specularIntensity} min={0} max={2} step={0.01} onChange={(v) => set("specularIntensity", v)} /></Row>
      <Row label="Specular Colour"><CompactColor value={params.specularColor} onChange={(v) => set("specularColor", v)} /></Row>

      </Group>
      <Group title="Clearcoat">
      <Row label="Clearcoat"><CompactSlider value={params.clearcoat} min={0} max={1} step={0.01} onChange={(v) => set("clearcoat", v)} /></Row>
      <Row label="Clearcoat Roughness"><CompactSlider value={params.clearcoatRoughness} min={0} max={1} step={0.01} onChange={(v) => set("clearcoatRoughness", v)} /></Row>

      </Group>
      <Group title="Sheen">
      <Row label="Sheen"><CompactSlider value={params.sheen} min={0} max={1} step={0.01} onChange={(v) => set("sheen", v)} /></Row>
      <Row label="Sheen Colour"><CompactColor value={params.sheenColor} onChange={(v) => set("sheenColor", v)} /></Row>
      <Row label="Sheen Roughness"><CompactSlider value={params.sheenRoughness} min={0} max={1} step={0.01} onChange={(v) => set("sheenRoughness", v)} /></Row>

      </Group>
      <Group title="Iridescence">
      <Row label="Iridescence"><CompactSlider value={params.iridescence} min={0} max={3} step={0.01} onChange={(v) => set("iridescence", v)} /></Row>
      <Row label="Iridescence IOR"><CompactSlider value={params.iridescenceIOR} min={0} max={3} step={0.01} onChange={(v) => set("iridescenceIOR", v)} /></Row>
      <Row label="Thickness Min"><CompactNumber value={params.iridescenceThicknessMin} min={0} max={500} step={1} onChange={(v) => set("iridescenceThicknessMin", v)} /></Row>
      <Row label="Thickness Max"><CompactNumber value={params.iridescenceThicknessMax} min={0} max={500} step={1} onChange={(v) => set("iridescenceThicknessMax", v)} /></Row>

      </Group>
      <Group title="Refraction">
      <Row label="Transmission"><CompactSlider value={params.transmission} min={0} max={1} step={0.01} onChange={(v) => set("transmission", v)} /></Row>
      <Row label="Thickness"><CompactSlider value={params.thickness} min={0} max={5} step={0.01} onChange={(v) => set("thickness", v)} /></Row>
      <Row label="Attenuation Colour"><CompactColor value={params.attenuationColor} onChange={(v) => set("attenuationColor", v)} /></Row>
      <Row label="Attenuation Distance"><CompactSlider value={params.attenuationDistance} min={0} max={10} step={0.01} onChange={(v) => set("attenuationDistance", v)} /></Row>

      </Group>
      <Group title="Anisotropy">
      <Row label="Anisotropy"><CompactSlider value={params.anisotropy} min={0} max={1} step={0.01} onChange={(v) => set("anisotropy", v)} /></Row>
      <Row label="Rotation"><CompactSlider value={params.anisotropyRotation} min={0} max={6.283} step={0.001} onChange={(v) => set("anisotropyRotation", v)} /></Row>

      </Group>
      <Group title="Emission">
      <Row label="Emissive Colour"><CompactColor value={params.emissive} onChange={(v) => set("emissive", v)} /></Row>
      <Row label="Emissive Intensity"><CompactSlider value={params.emissiveIntensity} min={0} max={5} step={0.01} onChange={(v) => set("emissiveIntensity", v)} /></Row>

      </Group>
      <Group title="Bump / Normal">
      <Row label="Bump Scale"><CompactNumber value={params.bumpScale} step={0.001} width={72} onChange={(v) => set("bumpScale", v)} /></Row>
      <Row label="Normal Scale X"><CompactNumber value={params.normalScaleX} step={0.01} width={72} onChange={(v) => set("normalScaleX", v)} /></Row>
      <Row label="Normal Scale Y"><CompactNumber value={params.normalScaleY} step={0.01} width={72} onChange={(v) => set("normalScaleY", v)} /></Row>
      <Row label="Displacement Scale"><CompactNumber value={params.displacementScale} step={0.001} width={72} onChange={(v) => set("displacementScale", v)} /></Row>
      </Group>
      <Group title="AO / Lightmap">
      <Row label="AO Map Intensity"><CompactSlider value={params.aoMapIntensity} min={0} max={2} step={0.01} onChange={(v) => set("aoMapIntensity", v)} /></Row>
      <Row label="Light Map Intensity"><CompactSlider value={params.lightMapIntensity} min={0} max={2} step={0.01} onChange={(v) => set("lightMapIntensity", v)} /></Row>
      </Group>
      <Group title="Blending">
      <Row label="Alpha Test"><CompactSlider value={params.alphaTest} min={0} max={1} step={0.001} onChange={(v) => set("alphaTest", v)} /></Row>
      <Row label="Depth Test"><CompactCheck checked={params.depthTest} onChange={(v) => set("depthTest", v)} /></Row>
      <Row label="Depth Write"><CompactCheck checked={params.depthWrite} onChange={(v) => set("depthWrite", v)} /></Row>
      <Row label="Color Write"><CompactCheck checked={params.colorWrite} onChange={(v) => set("colorWrite", v)} /></Row>
      <Row label="Polygon Offset"><CompactCheck checked={params.polygonOffset} onChange={(v) => set("polygonOffset", v)} /></Row>
      <Row label="Poly Offset Factor"><CompactNumber value={params.polygonOffsetFactor} step={0.1} width={72} onChange={(v) => set("polygonOffsetFactor", v)} /></Row>
      <Row label="Poly Offset Units"><CompactNumber value={params.polygonOffsetUnits} step={0.1} width={72} onChange={(v) => set("polygonOffsetUnits", v)} /></Row>
      </Group>
      <Group title="Environment">
      <Row label="Env Intensity"><CompactSlider value={params.envMapIntensity} min={0} max={10} step={0.01} onChange={(v) => set("envMapIntensity", v)} /></Row>
      </Group>
      {error ? <span className="px-3 pb-1.5 pt-1 text-[11px] text-ed-red">{error}</span> : null}
    </div>
  );
}

/**
 * One collapsible sub-accordion, matching the reference's nesting inside
 * Physical Material (Colors · Rough/Metal · Bump/Normal · Sheen · Clearcoat ·
 * Emission · Refraction · Blending · AO/Lightmap · Iridescence · Environment —
 * plus our Specular and Anisotropy, which it does not split out).
 *
 * Collapsed state rides the SAME persisted `collapsed` map as the sections
 * themselves, keyed `material.<group>` — no second state shape to keep in step,
 * and it survives a reload like everything else in the panel.
 *
 * Everything but Colors starts closed: this is fifty-odd rows, and opening all
 * of them at once is how it became a wall of sliders nobody scrolled.
 */
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  const [collapsedMap, setCollapsed] = useCollapsed();
  const key = `material.${title}`;
  const collapsed = collapsedMap[key] ?? title !== "Colors";
  return (
    <>
      <button
        type="button"
        onClick={() => setCollapsed(key, !collapsed)}
        className="flex items-center gap-1 px-2.5 pb-1 pt-3 text-left text-[12px] font-semibold text-ed-text-2 hover:text-ed-text"
      >
        <Icon name={collapsed ? "chevron_right" : "expand_more"} size={14} />
        {title}
      </button>
      {collapsed ? null : children}
    </>
  );
}

function Note({ text }: { text: string }) {
  return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">{text}</span>;
}
